/**
 * V1 Surveys API
 * 
 * API client for V1 event and session surveys.
 * These endpoints are public and support anonymous responses.
 */

import { BaseApi } from '@portal/api/apiBase';
import type { V1Survey, V1QuestionResponse } from '@portal/components/v1/questionnaires/shared';

// CRITICAL: No /api/portal prefix! BaseApi adds it automatically
const SURVEY_API_PATH = '/events/:event_slug/v1/surveys';
const SESSION_SURVEY_API_PATH = '/events/:event_slug/v1/days/:day_slug/sessions/:session_slug/surveys';

class V1SurveysApi extends BaseApi {
  // Get list of all surveys for V1 event
  async getSurveysList(eventSlug: string): Promise<any[]> {
    const path = SURVEY_API_PATH.replace(':event_slug', eventSlug);
    return this.get<any[]>(path);
  }

  // Event Surveys
  async getEventSurvey(eventSlug: string, surveySlug: string): Promise<V1Survey> {
    const path = `${SURVEY_API_PATH.replace(':event_slug', eventSlug)}/${surveySlug}`;
    return this.get<V1Survey>(path);
  }

  async submitEventSurvey(
    eventSlug: string, 
    surveySlug: string, 
    responses: V1QuestionResponse[]
  ): Promise<any> {
    const path = `${SURVEY_API_PATH.replace(':event_slug', eventSlug)}/${surveySlug}/responses`;
    // Use standard post - BaseApi handles camelCase to snake_case conversion
    return this.post(path, { 
      surveyResponse: { 
        questionResponsesAttributes: responses 
      } 
    });
  }

  // Session Surveys
  async getSessionSurvey(
    eventSlug: string, 
    daySlug: string,
    sessionSlug: string,
    surveySlug: string
  ): Promise<V1Survey> {
    const path = SESSION_SURVEY_API_PATH
      .replace(':event_slug', eventSlug)
      .replace(':day_slug', daySlug)
      .replace(':session_slug', sessionSlug) + `/${surveySlug}`;
    return this.get<V1Survey>(path);
  }

  async submitSessionSurvey(
    eventSlug: string,
    daySlug: string,
    sessionSlug: string,
    surveySlug: string,
    responses: V1QuestionResponse[]
  ): Promise<any> {
    const path = SESSION_SURVEY_API_PATH
      .replace(':event_slug', eventSlug)
      .replace(':day_slug', daySlug)
      .replace(':session_slug', sessionSlug) + `/${surveySlug}/responses`;
    // Use standard post - BaseApi handles camelCase to snake_case conversion
    return this.post(path, { 
      surveyResponse: { 
        questionResponsesAttributes: responses 
      } 
    });
  }
}

export const v1SurveysApi = new V1SurveysApi();